import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, getDocs, deleteDoc, doc, updateDoc, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";

const ADMIN_UID = "fy1HRZav9TW5MJFxHE1hc3mJFUl2";

function AdminPanel({ user }) {
  const [contests, setContests] = useState([]);
  const [selectedContest, setSelectedContest] = useState(null);
  const [entries, setEntries] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [computing, setComputing] = useState(false);
  const [syncResult, setSyncResult] = useState("");
  const [computeResult, setComputeResult] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => { if (open) fetchContests(); }, [open]);

  const fetchContests = async () => {
    setLoading(true);
    const snap = await getDocs(collection(db, "contests"));
    setContests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const fetchEntries = async (contestId) => {
    const q = query(collection(db, "entries"), where("contestId", "==", contestId));
    const snap = await getDocs(q);
    setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const selectContest = (contest) => { setSelectedContest(contest); fetchEntries(contest.id); };

  const deleteContest = async (contestId) => {
    if (!window.confirm("Delete this contest and all its entries?")) return;
    const q = query(collection(db, "entries"), where("contestId", "==", contestId));
    const snap = await getDocs(q);
    for (const d of snap.docs) await deleteDoc(doc(db, "entries", d.id));
    await deleteDoc(doc(db, "contests", contestId));
    setSelectedContest(null); setEntries([]);
    fetchContests();
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this entry?")) return;
    await deleteDoc(doc(db, "entries", entryId));
    fetchEntries(selectedContest.id);
  };

  const toggleLock = async (contest) => {
    await updateDoc(doc(db, "contests", contest.id), { picksLocked: !contest.picksLocked });
    fetchContests();
    if (selectedContest && selectedContest.id === contest.id) {
      setSelectedContest({ ...contest, picksLocked: !contest.picksLocked });
    }
  };

  // Live API sync — for real tournament
  const syncNow = async () => {
    setSyncing(true); setSyncResult("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "syncScoresNow")();
      setSyncResult("✓ Sync complete! Fixtures: " + result.data.fixtures + " · Teams: " + result.data.teams);
    } catch (err) { setSyncResult("✗ Sync failed: " + err.message); }
    setSyncing(false);
  };

  // Compute from existing Firestore data — for testing
  const computeFromDB = async () => {
    setComputing(true); setComputeResult("");
    try {
      const fn = getFunctions();
      const result = await httpsCallable(fn, "computeScoresFromDB")();
      setComputeResult("✓ Scores computed! Entries updated: " + result.data.entries + " · Teams found: " + result.data.teams);
    } catch (err) { setComputeResult("✗ Failed: " + err.message); }
    setComputing(false);
  };

  if (user.uid !== ADMIN_UID) return null;

  return (
    <div style={{ marginTop: 32 }}>
      <button onClick={() => setOpen(!open)} className="btn btn-ghost btn-sm" style={{ borderColor: "rgba(255,215,0,0.3)", color: "#FFD700" }}>
        {open ? "▲ Hide Admin Panel" : "⚙️ Admin Panel"}
      </button>

      {open && (
        <div style={{ marginTop: 12, border: "1px solid rgba(255,215,0,0.2)", borderRadius: 12, padding: 20, background: "rgba(255,215,0,0.03)" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#FFD700", letterSpacing: 2, marginBottom: 16 }}>ADMIN PANEL</div>

          {/* Score Sync */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 10 }}>SCORE SYNC</div>

            {/* Live API sync */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 10 }}>
              <button onClick={syncNow} disabled={syncing} className="btn btn-primary btn-sm">
                {syncing ? "Syncing..." : "⚡ Sync from API (Live)"}
              </button>
              {syncResult && <span style={{ fontSize: 13, color: syncResult.startsWith("✓") ? "#00C853" : "#E53935" }}>{syncResult}</span>}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", marginBottom: 12 }}>
              Fetches live data from football API. Use during real tournament.
            </div>

            {/* Compute from DB */}
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
              <button onClick={computeFromDB} disabled={computing} className="btn btn-success btn-sm">
                {computing ? "Computing..." : "🧮 Compute Scores from DB"}
              </button>
              {computeResult && <span style={{ fontSize: 13, color: computeResult.startsWith("✓") ? "#00C853" : "#E53935" }}>{computeResult}</span>}
            </div>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.3)" }}>
              Scores entries using teamStats already in Firestore. Use after loading test data.
            </div>
          </div>

          {/* Contests + entries */}
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 10 }}>ALL CONTESTS</div>
              {loading && <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>Loading...</p>}
              {contests.map((c) => (
                <div key={c.id} onClick={() => selectContest(c)}
                  style={{ border: "1px solid " + (selectedContest && selectedContest.id === c.id ? "rgba(255,215,0,0.5)" : "rgba(255,255,255,0.08)"), borderRadius: 8, padding: "10px 12px", marginBottom: 8, cursor: "pointer", background: selectedContest && selectedContest.id === c.id ? "rgba(255,215,0,0.05)" : "transparent" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 2 }}>
                        {c.joinCode} · {c.picksLocked ? "🔒 Locked" : "🔓 Open"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={(e) => { e.stopPropagation(); toggleLock(c); }} className="btn btn-ghost btn-sm">
                        {c.picksLocked ? "Unlock" : "Lock"}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); deleteContest(c.id); }} className="btn btn-danger btn-sm">Del</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {selectedContest && (
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 10 }}>
                  ENTRIES — {selectedContest.name.toUpperCase()}
                </div>
                {entries.length === 0 && <p style={{ color: "rgba(255,255,255,0.3)", fontSize: 13 }}>No entries yet.</p>}
                {entries.map((e) => (
                  <div key={e.id} style={{ border: "1px solid rgba(255,255,255,0.08)", borderRadius: 8, padding: "8px 12px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{e.entryName}</div>
                      <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>{e.userName} · {e.totalScore || 0} pts</div>
                    </div>
                    <button onClick={() => deleteEntry(e.id)} className="btn btn-danger btn-sm">Del</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminPanel;
